'use client'

import { useTranslations } from 'next-intl'
import { DownloadIcon, EyeIcon, FileTextIcon } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Button } from '@/components/ui/button'
import { ContentFileIcon } from '@/components/partials'
import { formatFileSize, getFileShowUrl, textLimit, tOptional } from '@/lib/utils'
import { useFilesStore } from '@/store'
import { FileType } from '@/types/file'

type PropsType = {
  file?: FileType
  title?: string
  download?: boolean
  preview?: boolean
}

const ContentDetailFile = ({ file, title, download = true, preview = false }: PropsType) => {
  const t = useTranslations()
  const addToDownloadQueue = useFilesStore(state => state.addToDownloadQueue)

  const handlePreview = () => {
    if (file?.path) {
      window.open(getFileShowUrl(file.path), '_blank', 'noopener,noreferrer')
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileTextIcon className="h-5 w-5" />
          {title ? tOptional(t, title) : tOptional(t, 'file')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {file ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center justify-between rounded-md border p-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-9 shrink-0">
                    <ContentFileIcon file={file} />
                  </div>
                  <div>
                    <p className="font-medium">{textLimit(file.name, 20)}</p>
                    <p className="text-muted-foreground text-sm">{formatFileSize(file.size)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {preview && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePreview}
                      disabled={!file.path}
                      className="flex items-center gap-2">
                      <EyeIcon className="h-4 w-4" />
                      <span className="hidden lg:inline">{t('preview')}</span>
                    </Button>
                  )}
                  {download && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => addToDownloadQueue(file)}
                      className="flex items-center gap-2">
                      <DownloadIcon className="h-4 w-4" />
                      <span className="hidden lg:inline">{t('download')}</span>
                    </Button>
                  )}
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent>{file.name}</TooltipContent>
          </Tooltip>
        ) : (
          <p className="text-muted-foreground">{t('no_file')}</p>
        )}
      </CardContent>
    </Card>
  )
}

export default ContentDetailFile
